import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/PermissionManagement.css';

const PermissionManagement = () => {
  const [permissions, setPermissions] = useState({});
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('permissions');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newPermission, setNewPermission] = useState({
    permission_name: '',
    permission_label: '',
    description: '',
    category: ''
  });

  useEffect(() => {
    fetchPermissions();
    fetchPendingRequests();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/permissions', {
        headers: {
          'X-Session-Token': localStorage.getItem('session_token') || localStorage.getItem('app_session_token') || ''
        }
      });

      if (response.data) {
        // Normalize response that may be either { permissions: {category: [...] } } or an array of permissions
        let raw = response.data.permissions ?? response.data;

        const grouped = {};

        if (Array.isArray(raw)) {
          raw.forEach(p => {
            const cat = p.category || p.group || 'Other';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(p);
          });
        } else if (raw && typeof raw === 'object') {
          // assume already grouped by category
          Object.keys(raw).forEach(k => {
            grouped[k] = Array.isArray(raw[k]) ? raw[k] : [];
          });
        }

        // If ticket.scanner exists anywhere in the raw response but not grouped, add it under 'Other'
        const existsScanner = Object.values(grouped).some(arr => arr.some(p => (p.permission_name || p.name || p) === 'ticket.scanner'));
        if (!existsScanner) {
          // also check if raw (array) contains the scanner permission
          if (Array.isArray(raw)) {
            const found = raw.find(p => (p.permission_name || p.name || p) === 'ticket.scanner');
            if (found) {
              if (!grouped['Other']) grouped['Other'] = [];
              grouped['Other'].unshift(found);
            }
          }
        }

        setPermissions(grouped);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setMessage('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await axios.get('/api/permission-requests/pending', {
        headers: {
          'X-Session-Token': localStorage.getItem('session_token') || localStorage.getItem('app_session_token') || ''
        }
      });
      if (response.data.success) {
        setPendingRequests(response.data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const handleCreatePermission = async (e) => {
    e.preventDefault();

    if (!newPermission.permission_name || !newPermission.permission_label) {
      setMessage('Permission name and label are required');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/permissions', newPermission, {
        headers: {
          'X-Session-Token': localStorage.getItem('session_token') || localStorage.getItem('app_session_token') || ''
        }
      });
      if (response.data.success) {
        setMessage('✅ Permission created successfully');
        setNewPermission({ permission_name: '', permission_label: '', description: '', category: '' });
        fetchPermissions();
      }
    } catch (error) {
      console.error('Error creating permission:', error);
      setMessage('Failed to create permission');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/permission-requests/${requestId}/approve`, {}, {
        headers: {
          'X-Session-Token': localStorage.getItem('session_token') || localStorage.getItem('app_session_token') || ''
        }
      });
      if (response.data.success) {
        setMessage('✅ Permission approved successfully');
        fetchPendingRequests();
      }
    } catch (error) {
      console.error('Error approving permission:', error);
      setMessage('Failed to approve permission');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async (requestId) => {
    const reason = prompt('Enter rejection reason:');
    if (reason === null) return;

    try {
      setLoading(true);
      const response = await axios.post(`/api/permission-requests/${requestId}/reject`, { reason }, {
        headers: {
          'X-Session-Token': localStorage.getItem('session_token') || localStorage.getItem('app_session_token') || ''
        }
      });
      if (response.data.success) {
        setMessage('✅ Permission rejected successfully');
        fetchPendingRequests();
      }
    } catch (error) {
      console.error('Error rejecting permission:', error);
      setMessage('Failed to reject permission');
    } finally {
      setLoading(false);
    }
  };

  const categories = Object.keys(permissions).sort();

  return (
    <div className="permission-management-container">
      <div className="permission-header">
        <h2>🔐 Permission Management</h2>
        <p>Manage system permissions and user access</p>
      </div>

      {message && (
        <div className="alert alert-info" role="alert">
          {message}
          <button
            className="btn-close"
            onClick={() => setMessage('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      <ul className="nav nav-tabs" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
            type="button"
            role="tab"
          >
            Permissions ({categories.length})
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
            type="button"
            role="tab"
          >
            Pending Requests ({pendingRequests.length})
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
            type="button"
            role="tab"
          >
            Create Permission
          </button>
        </li>
      </ul>

      <div className="tab-content">
        {/* Permissions Tab */}
        {activeTab === 'permissions' && (
          <div className="tab-pane active">
            <div className="permission-content">
              <h3>Available Permissions</h3>
              {loading ? (
                <div className="loading-spinner">Loading...</div>
              ) : categories.length > 0 ? (
                <div className="permissions-grid">
                  {categories.map((category) => (
                    <div key={category} className="permission-category">
                      <h4>{category}</h4>
                      <div className="permission-list">
                        {permissions[category].map((perm) => (
                          <div key={perm.id} className="permission-item">
                            <div className="permission-info">
                              <strong>{perm.permission_label}</strong>
                              <code>{perm.permission_name}</code>
                              {perm.description && (
                                <p className="permission-desc">{perm.description}</p>
                              )}
                            </div>
                            <span
                              className={`badge ${perm.is_active ? 'bg-success' : 'bg-danger'}`}
                            >
                              {perm.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No permissions found</p>
              )}
            </div>
          </div>
        )}

        {/* Pending Requests Tab */}
        {activeTab === 'requests' && (
          <div className="tab-pane active">
            <div className="requests-content">
              <h3>Pending Permission Requests</h3>
              {loading ? (
                <div className="loading-spinner">Loading...</div>
              ) : pendingRequests.length > 0 ? (
                <div className="requests-table">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Permission</th>
                        <th>Category</th>
                        <th>Requested By</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRequests.map((req) => (
                        <tr key={req.id}>
                          <td>
                            <strong>{req.first_name} {req.last_name}</strong>
                          </td>
                          <td>{req.email}</td>
                          <td>
                            <strong>{req.permission_label}</strong>
                            <br />
                            <small className="text-muted">{req.permission_name}</small>
                          </td>
                          <td>
                            <span className="badge bg-secondary">{req.category}</span>
                          </td>
                          <td>{req.requested_by_name || 'Admin'}</td>
                          <td>
                            <small>
                              {new Date(req.granted_at).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleApproveRequest(req.id)}
                              disabled={loading}
                            >
                              ✓ Approve
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleRejectRequest(req.id)}
                              disabled={loading}
                            >
                              ✕ Reject
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No pending requests</p>
              )}
            </div>
          </div>
        )}

        {/* Create Permission Tab */}
        {activeTab === 'create' && (
          <div className="tab-pane active">
            <div className="create-permission-content">
              <h3>Create New Permission</h3>
              <form onSubmit={handleCreatePermission} className="create-form">
                <div className="form-group">
                  <label htmlFor="permission_name">Permission Name (slug)*</label>
                  <input
                    type="text"
                    id="permission_name"
                    className="form-control"
                    placeholder="e.g., event.create"
                    value={newPermission.permission_name}
                    onChange={(e) =>
                      setNewPermission({
                        ...newPermission,
                        permission_name: e.target.value,
                      })
                    }
                    required
                  />
                  <small className="form-text text-muted">
                    Use format: resource.action (e.g., event.create, user.edit)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="permission_label">Permission Label*</label>
                  <input
                    type="text"
                    id="permission_label"
                    className="form-control"
                    placeholder="e.g., Create Event"
                    value={newPermission.permission_label}
                    onChange={(e) =>
                      setNewPermission({
                        ...newPermission,
                        permission_label: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    className="form-control"
                    placeholder="Describe what this permission allows"
                    value={newPermission.description}
                    onChange={(e) =>
                      setNewPermission({
                        ...newPermission,
                        description: e.target.value,
                      })
                    }
                    rows="3"
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    className="form-control"
                    value={newPermission.category}
                    onChange={(e) =>
                      setNewPermission({
                        ...newPermission,
                        category: e.target.value,
                      })
                    }
                  >
                    <option value="">Select or enter category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Choose existing category or type a new one
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Permission'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionManagement;
